import { PayloadAction } from "@reduxjs/toolkit";
import { createApi, createSlice } from "utils/@reduxjs/toolkit";
import { useInjectReducer } from "utils/redux-injectors";
import { ReferralListState, ReferralListResponse, NotificationListResponse } from "./types";
import { baseQuery, endpoints, formatErrors } from 'utils/api/endpoints';

export const initialState: ReferralListState = {
  isLoading: false,
  error: null,
  data: null,
};

// RTK Query API for referrals
export const api = createApi({
  reducerPath: 'referralListApi',
  tagTypes: ['Referral', 'Notification', 'Note'],
  baseQuery,
  endpoints: builder => ({
    // Get all referrals
    getReferrals: builder.query<ReferralListResponse, { 
      name?: string; 
      status?: string[]; 
      startDate?: string; 
      endDate?: string; 
      priority?: string[];
      page?: number;
      limit?: number;
      timezone?: string;
    }>({
      query: ({ name, status, startDate, endDate, priority, page = 1, limit = 20, timezone }) => {
        const params = new URLSearchParams();
        
        if (name) params.append('name', name);
        if (status && status.length > 0) params.append('status', JSON.stringify(status));
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (priority && priority.length > 0) params.append('priority', JSON.stringify(priority));
        params.append('page', page.toString());
        params.append('timezone', timezone);
        params.append('limit', limit.toString());
        
        const queryString = params.toString();
        const url = queryString ? `${endpoints.referralList.url}?${queryString}` : endpoints.referralList.url;
        
        return {
          url,
          method: endpoints.referralList.method,
        };
      },
      transformResponse: (response: any) => response || null,
      transformErrorResponse(baseQueryReturnValue, meta, arg) {
        return formatErrors(baseQueryReturnValue.data);
      },
      providesTags: ['Referral'],
    }),
    // Get a referral by ID
    getReferralById: builder.query<any, { referralId: string }>({
      query: ({ referralId }) => ({
        url: endpoints.referralGetById.url.replace(':referralId', referralId),
        method: endpoints.referralGetById.method,
      }),
      transformResponse: (response: any) => response || null,
    }),
    // Update a referral
    updateReferral: builder.mutation<any, { referralId: string; data: any }>({
      query: ({ referralId, data }) => ({
        url: endpoints.referralUpdate.url.replace(':referralId', referralId),
        method: endpoints.referralUpdate.method,
        body: data,
      }),
      invalidatesTags: ['Referral'],
    }),
    // Create a referral note
    createReferralNote: builder.mutation<any, { referralId: string; data: any }>({
      query: ({ referralId, data }) => ({
        url: endpoints.noteAddToReferral.url.replace(':referralId', referralId),
        method: endpoints.noteAddToReferral.method,
        body: data,
      }),
      invalidatesTags: ['Note'],
    }),
    // Get notes list by referral
    noteListByReferral: builder.query<any, { referralId: string }>({
      query: ({ referralId }) => ({
        url: endpoints.noteListByReferral.url.replace(':referralId', referralId),
        method: endpoints.noteListByReferral.method,
      }),
      transformResponse: (response: any) => response || null,
      providesTags: ['Note'],
    }),
    // Get notification list
    notificationList: builder.query<NotificationListResponse, any>({
      query: (params) => ({
        url: endpoints.notificationList.url,
        method: endpoints.notificationList.method,
        params,
      }),
      transformResponse: (response: any) => {
        if (!response) return { data: [], pagination: null };
        
        // Handle the new API response structure with nested data.documents
        if (response.data?.documents && response.data?.pageNumber !== undefined) {
          return {
            data: response.data.documents,
            pagination: {
              documents: response.data.documents,
              pageNumber: response.data.pageNumber,
              pageSize: response.data.pageSize,
              totalDocuments: response.data.totalDocuments,
              totalPages: response.data.totalPages,
              // Legacy support
              hasNextPage: response.data.pageNumber < response.data.totalPages,
              hasPrevPage: response.data.pageNumber > 1,
              limit: response.data.pageSize,
              nextPage: response.data.pageNumber < response.data.totalPages ? response.data.pageNumber + 1 : null,
              page: response.data.pageNumber,
              prevPage: response.data.pageNumber > 1 ? response.data.pageNumber - 1 : null,
              total: response.data.totalDocuments,
            }
          };
        }
        
        // Handle the flat structure (documents at root level)
        if (response.documents && response.pageNumber !== undefined) {
          return {
            data: response.documents,
            pagination: {
              documents: response.documents,
              pageNumber: response.pageNumber,
              pageSize: response.pageSize,
              totalDocuments: response.totalDocuments,
              totalPages: response.totalPages,
              // Legacy support
              hasNextPage: response.pageNumber < response.totalPages,
              hasPrevPage: response.pageNumber > 1,
              limit: response.pageSize,
              nextPage: response.pageNumber < response.totalPages ? response.pageNumber + 1 : null,
              page: response.pageNumber,
              prevPage: response.pageNumber > 1 ? response.pageNumber - 1 : null,
              total: response.totalDocuments,
            }
          };
        }
        
        // Fallback for old structure
        return response || { data: [], pagination: null };
      },
      providesTags: ['Notification'],
    }),
    // Mark notification as read
    markNotificationAsRead: builder.mutation<any, { notificationId: string }>({
      query: ({ notificationId }) => ({
        url: endpoints.notificationMarkAsRead.url.replace(':notificationId', notificationId),
        method: endpoints.notificationMarkAsRead.method,
      }),
      invalidatesTags: ['Notification'],
    }),
    // Delete notification
    deleteNotification: builder.mutation<any, { notificationId: string }>({
      query: ({ notificationId }) => ({
        url: endpoints.notificationDelete.url.replace(':notificationId', notificationId),
        method: endpoints.notificationDelete.method,
      }),
      invalidatesTags: ['Notification'],
    }),
    // Add referral
    addReferral: builder.mutation<any, any>({
      query: (body: any) => ({
        url: endpoints.referralCreate.url,
        method: endpoints.referralCreate.method,
        body: body,
      }),
      transformResponse: (response: any) => response || null, 
      transformErrorResponse(baseQueryReturnValue, meta, arg) {
        return formatErrors(baseQueryReturnValue.data);
      },
      invalidatesTags: ['Referral'],
    }),
  }),
});

const slice = createSlice({
  name: "referralList",
  initialState,
  reducers: {
    someAction(state, action: PayloadAction<any>) {},
  },
});

export const { actions: referralListActions, reducer: referralListReducer } = slice;

export const useReferralListSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });

  // RTK Query injects itself
  return {
    actions: slice.actions,
    ...api,
  };
};

/**
 * Example Usage:
 *
 * export function MyComponentNeedingThisSlice() {
 *  const { actions } = useSlice();
 *
 *  const onButtonClick = (evt) => {
 *    dispatch(actions.someAction());
 *   };
 * }
 */
